import { useQuery } from "react-query";

import {
  getDesignStepOptions,
  DesignSelectionApiRequestBody,
} from "../apis/designElementsApi";
import { DESIGN_STEP_OPTIONS_QUERY_KEY } from "../queries-keys/designElements";
import useSnackbar from "../components/snackbar/hooks/useSnackbar";
import { getErrorMessage } from "../utils/errors";
import Shape from "../models/furniture/Shape";
import Material from "../models/furniture/Material";

interface UseGetDesignStepOptionsParams extends DesignSelectionApiRequestBody {
  enabled?: boolean;
  onSuccess?: (data: {
    shapes?: Shape[] | undefined;
    materials?: Material[];
  }) => void;
}

const useGetDesignStepOptions = ({
  design_composition,
  layout,
  step_info,
  enabled = true,
  onSuccess,
}: UseGetDesignStepOptionsParams) => {
  const { handleOpenSnackbar } = useSnackbar();

  const {
    data: designStepOptions,
    isLoading: isGettingDesignOptions,
    error: getDesignOptionsError,
    refetch: refetchOptions,
    isFetching: isRefetchingDesignOptions,
  } = useQuery(
    DESIGN_STEP_OPTIONS_QUERY_KEY(layout, step_info),
    () =>
      getDesignStepOptions({
        design_composition,
        layout,
        step_info,
      }),
    {
      enabled: enabled,
      refetchOnMount: false,
      retryOnMount: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      onError: (error) => {
        handleOpenSnackbar(getErrorMessage(error), "error");
      },
      onSuccess: (data) => {
        onSuccess?.(data);
      },
    }
  );

  return {
    designStepOptions,
    isGettingDesignOptions,
    getDesignOptionsError,
    refetchOptions,
    isRefetchingDesignOptions,

    shapes: designStepOptions?.shapes || [],
    materials: designStepOptions?.materials || [],
    isShapesStep: (designStepOptions?.shapes?.length || 0) > 0,
    isMaterialsStep: (designStepOptions?.materials?.length || 0) > 0,
  };
};

export default useGetDesignStepOptions;
